//@ts-check
import { ApolloClient, ApolloLink, from, HttpLink, HttpOptions, InMemoryCache } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { PRODUCT_SERVICE_HOSTNAME, SiteSettings } from 'src/scripts/utils/environment';
import { heapTrack } from '../../utils/helpers';
import PsFragmentMatch from '../../utils/graphql/ps-fragment-matcher.json';
import { ShippingCountryState } from 'src/scripts/utils/shipping';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { notNil } from 'src/scripts/utils/typescriptUtils';

export const graphqlHost = (() => {
  return 'https://' + window.Shopify.shop;
})();

export const StorefrontAccessToken = process.env.REACT_APP_SHOPIFY_PRIVATE_APP_STOREFRONT_TOKEN;
// ? [REVISIT] figure out how to make this crash at compile time
if (!StorefrontAccessToken) throw new Error('Missing storefront access token, check .env');

export const NETWORK_CONFIG: HttpOptions = {
  uri: (operation) => `${graphqlHost}/api/2025-01/graphql.json?opName=${operation.operationName}`,
  headers: {
    'X-Shopify-Storefront-Access-Token': StorefrontAccessToken,
  },
};

export const PRODUCT_SERVICE_CONFIG: HttpOptions = {
  uri: (operation) => `https://${PRODUCT_SERVICE_HOSTNAME}/graphql?opName=${operation.operationName}`,
};

export const RETRY_LINK_CONFIG: RetryLink.Options = {
  // ? [REVISIT] optimize & make sure we don't hit any rate-limit issues
  delay: {
    initial: 300,
    max: Infinity,
    jitter: true,
  },
  attempts: {
    // ? [REVISIT] is it worth trying 1 fix at a time
    max: 1, //5,
    retryIf: (error: Error, operation) => {
      heapTrack('Apollo Networking Error', {
        error: error?.message,
        operationName: operation.operationName,
        variables: operation.variables,
        pathname: window.location.pathname,
      });
      return !!error;
    },
  },
};

export const createApolloClientWithCache = (cache?: InMemoryCache) => {
  const link = from([new RetryLink(RETRY_LINK_CONFIG), addCountryCodeLink, new HttpLink(NETWORK_CONFIG)]);
  const client = new ApolloClient({
    link,
    cache: cache || new InMemoryCache(),
  });
  return client;
};

const createApolloClientWithCacheProductService = () => {
  const link = from(
    [
      new RetryLink(RETRY_LINK_CONFIG),
      shouldUseApqForPS() ? createPersistedQueryLink({ sha256, useGETForHashedQueries: true }) : null,
      addCountryCodeLink,
      new HttpLink(PRODUCT_SERVICE_CONFIG),
    ].filter(notNil)
  );
  const client = new ApolloClient({
    link,
    cache: new InMemoryCache({
      possibleTypes: PsFragmentMatch.possibleTypes,
      typePolicies: {
        BrFilter: {
          keyFields: false,
        },
        IListFilterValue: {
          keyFields: false,
        },
        Query: {
          fields: {
            searchProducts: {
              keyArgs: ['query', 'filters', 'sortKey'],
              merge(existing, incoming) {
                if (incoming.__typename === 'AProductConnection') {
                  return {
                    ...incoming,
                    nodes: [...(existing?.nodes ?? []), ...(incoming.nodes ?? [])],
                  };
                } else {
                  return incoming;
                }
              },
            },
          },
        },
        ProductCategory: {
          fields: {
            products: {
              keyArgs: ['filters', 'sortKey'],
              merge(existing, incoming) {
                if (incoming.__typename === 'ProductList') {
                  return {
                    ...incoming,
                    nodes: [...(existing?.nodes ?? []), ...(incoming.nodes ?? [])],
                  };
                } else {
                  return incoming;
                }
              },
            },
          },
        },
      },
    }),
  });
  return client;
};

const createApolloClientWithCacheBuilderCarousel = () => {
  const link = from(
    [
      new RetryLink(RETRY_LINK_CONFIG),
      shouldUseApqForPS() ? createPersistedQueryLink({ sha256, useGETForHashedQueries: true }) : null,
      addCountryCodeLink,
      new HttpLink(PRODUCT_SERVICE_CONFIG),
    ].filter(notNil)
  );
  const client = new ApolloClient({
    link,
    cache: new InMemoryCache({
      possibleTypes: PsFragmentMatch.possibleTypes,
      typePolicies: {
        ProductCategory: {
          fields: {
            products: {
              keyArgs: ['handle', 'limit'],
              merge(existing, incoming) {
                return incoming;
              },
            },
          },
        },
      },
    }),
  });

  return client;
};

// util for (A)PQ
async function sha256(message: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}

const shouldUseApqForPS = () => {
  return isSubtleCryptoDigestSupported() && SiteSettings?.enableAPQforPS === true;
};

function isSubtleCryptoDigestSupported() {
  return (
    typeof window.crypto === 'object' &&
    typeof window.crypto.subtle === 'object' &&
    typeof window.crypto.subtle.digest === 'function'
  );
}

// Custom Apollo Client Links
const addCountryCodeLink = new ApolloLink((operation, forward) => {
  const { query, variables = {} } = operation;
  const shouldMutateVariables = query.loc?.source.body.includes('$countryCode: CountryCode');
  const countryCodeValue = ShippingCountryState.countryCode?.toUpperCase();

  if (shouldMutateVariables && countryCodeValue && !variables.countryCode) {
    operation.variables = {
      ...variables,
      countryCode: countryCodeValue,
    };
  }

  return forward(operation);
});

export const productServiceClient = createApolloClientWithCacheProductService();
export const PSClientBuilder = createApolloClientWithCacheBuilderCarousel();
